import { Injectable } from '@angular/core';
import { OpenLayerStyleService } from './open-layer-style.service';
import GeometryTypeEnum from '@app/models-internal/enums/geometry-type.enum';

/**
 * Point Feature Map Icon
 */
interface PointFeatureIcon {
  /**
   * The name of the icon type (e.g. treestand, cabin, parking)
   */
  name: string;

  /**
   * The Full url to  the image to use as the icon
   */
  iconUrl: string;

  /**
   * The base map for which an icon should be used. Only used when the object exists in the baseMapOverrides array
   */
  baseMapName?: string;

  baseMapOverrides?: PointFeatureIcon[];
}

export default PointFeatureIcon;

/**
 * Shared Map Configuration Information
 */
@Injectable()
export class MapConfigurationService {
  private readonly _componentName = 'MapConfigurationService';

  private readonly _pointFeatureTypeNames = ['trailcam', 'cabin', 'parking', 'camp', 'treestand', 'blind', 'gate', 'photo','stand'];

  private readonly _lineFeatureTypeNames = ['trail', 'path'];

  private readonly _polyFeatureTypeNames = ['boundary'];

  /**
   * Determines the geometry type for a feature type
   */
  public getGeometryType(featureType: string) {
    const v = featureType.toLowerCase();

    if (this._pointFeatureTypeNames.includes(v)) {
      return GeometryTypeEnum.Point;
    }

    if (this._lineFeatureTypeNames.includes(v)) {
      return GeometryTypeEnum.Line;
    }

    if (this._polyFeatureTypeNames.includes(v)) {
      return GeometryTypeEnum.Polygon;
    }

    return GeometryTypeEnum.Unknown;
  }

  /**
   * Shared Icon Types for the OA Mapping components (Google and OpenLayers)
   */
  private _featureIcons = [
    {
      name: 'treestand',
      iconUrl: 'https://cdn.outdooraccess.com/oa-assets/map-icons/treestand.png',
      baseMapOverrides: [
        {
          name: 'treestand',
          baseMapName: 'Aerials',
          iconUrl: 'https://cdn.outdooraccess.com/oa-assets/map-icons/treestand.png'
        }
      ]
    },

    {
      name: 'stand',
      iconUrl: 'https://cdn.outdooraccess.com/oa-assets/map-icons/treestand.png',
      baseMapOverrides: [
        {
          name: 'stand',
          baseMapName: 'Aerials',
          iconUrl: 'https://cdn.outdooraccess.com/oa-assets/map-icons/treestand.png'
        }
      ]
    },
    
    
    {
      name: 'cabin',
      iconUrl: 'https://cdn.outdooraccess.com/oa-assets/map-icons/cabin-icon.png',
      baseMapOverrides: [
        {
          name: 'cabin',
          baseMapName: 'Aerials',
          iconUrl: 'https://cdn.outdooraccess.com/oa-assets/map-icons/cabin-icon-aerials.png'
        }
      ]
    },
    {
      name: 'parking',
      iconUrl: 'https://cdn.outdooraccess.com/oa-assets/map-icons/parking.png',
      baseMapOverrides: [
        {
          name: 'parking',
          baseMapName: 'Aerials',
          iconUrl: 'https://cdn.outdooraccess.com/oa-assets/map-icons/parking.png'
        }
      ]
    },
    {
      name: 'trailcam',
      iconUrl: 'https://cdn.outdooraccess.com/oa-assets/map-icons/trailcam-icon.png'
    },

    {
      name: 'photo',
      iconUrl: 'https://cdn.outdooraccess.com/oa-assets/map-icons/photo.png'
    },

    {
      name: 'gate',
      iconUrl: 'https://cdn.outdooraccess.com/oa-assets/map-icons/gate.png',
      baseMapOverrides: [
        {
          name: 'gate',
          baseMapName: 'Aerials',
          iconUrl: 'https://cdn.outdooraccess.com/oa-assets/map-icons/gate.png'
        }
      ]
    },

    {
      name: 'blind',
      iconUrl: 'https://cdn.outdooraccess.com/oa-assets/map-icons/blind.png',
      baseMapOverrides: [
        {
          name: 'blind',
          baseMapName: 'Aerials',
          iconUrl: 'https://cdn.outdooraccess.com/oa-assets/map-icons/blind.png'
        }
      ]
    },
    {
      name: 'camp',
      iconUrl: 'https://cdn.outdooraccess.com/oa-assets/map-icons/campsite.png',
      baseMapOverrides: [
        {
          name: 'camp',
          baseMapName: 'Aerials',
          iconUrl: 'https://cdn.outdooraccess.com/oa-assets/map-icons/campsite.png'
        }
      ]
    },

    {
      name: 'fdc-deer-square',
      iconUrl: 'https://cdn.outdooraccess.com/oa-assets/map-icons/fdc-deer-square.svg',
      baseMapOverrides: [
        {
          name: 'fdc-deer-square',
          baseMapName: 'Aerials',
          iconUrl: 'https://cdn.outdooraccess.com/oa-assets/map-icons/fdc-deer-square.svg'
        }
      ]
    }
  ] as PointFeatureIcon[];

  /**
   * Default point icon
   */
  public defaultPointIcon: PointFeatureIcon = {
    name: 'default',
    iconUrl: 'https://cdn.outdooraccess.com/oa-assets/map-icons/unknown-icon.png'
  };

  /**
   * Point feature icons to use in maps
   */
  public get featureIcons(): PointFeatureIcon[] {
    return this._featureIcons;
  }

  constructor(private styleService: OpenLayerStyleService) {
    // Hack to make the icon types accessible from the Open Layers style function
    // The style function will read/parse the icon types from local storage
    // Use let featureIcons = JSON.parse(localStorage.getItem('featureIcons')) as PointFeatureIcon[]; to get these items
    localStorage.setItem('featureIcons', JSON.stringify(this.featureIcons));
    localStorage.setItem('defaultPointIcon', JSON.stringify(this.defaultPointIcon));
  }

  /**
   * Gets an Icon for feature type using the base map name
   */
  public getIconUrl(icons: PointFeatureIcon[], featureType: string, baseMapName: string) {
    // console.log(`MapConfigurationService.getIconUrl featureType=${featureType}, baseMapName=${baseMapName}, icons:`,icons);

    let icon = icons.find(x => x.name == featureType);

    // if there's no base map name, return the PointFeatureIcon.iconUrl value
    if (!baseMapName) {
      return icon.iconUrl;
    }

    // even if there is a base map name, if there are no baseMapOverrides we will still return the PointFeatureIcon.iconUrl value
    if (!icon.baseMapOverrides || icon.baseMapOverrides.length == 0) {
      return icon.iconUrl;
    }

    // attempt to obtain an override icon
    let iconOverride = icon.baseMapOverrides.find(x => x.name == featureType && x.baseMapName == baseMapName);

    if (iconOverride) {
      return iconOverride.iconUrl;
    }

    // if no iconOverride, return PointFeatureIcon.iconUrl value
    return icon.iconUrl;
  }

  /**
   * Gets the feature style (Open Layers)
   * @param featureType - Feature type name (e.g. photo, gate, stand)
   * @param baseMapName - Name of the base map - case sensitive
   */
  public getFeatureStyle(featureType: string, baseMapName: string) {
    // console.log(`${this._componentName}.getFeatureStyle featureType=${featureType},baseMapName=${baseMapName}.`);

    const lc = featureType.toLowerCase();

    const geometryType = this.getGeometryType(lc);

    switch (geometryType) {
      case GeometryTypeEnum.Point:
        const icons = this.featureIcons.filter(x => x.name === lc);

        if (icons.length === 0) {
          
        }

        return this.styleService.createIconStyle(this.getIconUrl(icons, lc, baseMapName), 0.21);
      case GeometryTypeEnum.Line:
        // there is only 2 types of line features, path and trail, and we are symbolizing the same way to the member
        return this.styleService.trailStyle;
      case GeometryTypeEnum.Polygon:
        return this.styleService.boundaryStyle;
      default:
        console.error(`Unknown Geometry Type for featureType=${featureType},baseMapName=${baseMapName}`);

        break;
    }
  }

  /**
   * Gets the functional feature type value for the feature.  If no featureType, pointType is returned. If no pointType, lineType is returned.
   */
  // tslint:disable-next-line: no-any
  public getFeatureType(feature: any) {
    if (!feature) {
      return null;
    }

    const featureType = (feature.get('featureType') || feature.get('pointType') || feature.get('lineType')) + '';

    return featureType;
  }
}
